import { useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Route,
  Switch,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';

import OrganizationManagementSidebar from './OrganizationManagementSidebar';
import OrganizationManagementDetails from './OrganizationManagementDetails';
import OrganizationJoinRequests from './OrganizationJoinRequests';
import OrganizationSubscriptions from './OrganizationSubscriptions';

import { useOrganizationUpdaterProvider } from '../../../context_providers/organization_context/OrganizationProvider';
import { useUserContext } from '../../../context_providers/user_context/UserProvider';
import { useUserUpdaterProvider } from '../../../context_providers/user_context/UserUpdaterProvider';
import { useLoading } from '../../../context_providers/loading_context/LoadingContext';
import { useBannerHeight } from '../../../context_providers/banner_context/BannerHeightContext';

import Loading from '../../../components/loading/Loading';
import COLORS from '../../../resources/style/Themes';
import { StringKeys } from '../../../resources/strings/StringKeys';

export const OrganizationManagement = () => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const { userExists } = useUserContext();
  const { organization } = useOrganizationUpdaterProvider();
  const { orgUser } = useUserUpdaterProvider();
  const { setPageLoading } = useLoading();
  const { defaultPageBoxPadding } = useBannerHeight();

  const mobileView: 'details' | 'join-requests' | 'subscriptions' =
    location.pathname === '/OrganizationManagement/OrganizationJoinRequests'
      ? 'join-requests'
      : location.pathname === '/OrganizationManagement/OrganizationSubscriptions'
        ? 'subscriptions'
        : 'details';
  const isDesktopJoinRequestsView = !isMobile && mobileView === 'join-requests';

  useEffect(() => {
    if (!userExists) {
      history.push('/');
    }
  }, [userExists, history]);

  useEffect(() => {
    if (orgUser && organization) {
      setPageLoading(false);
    }
  }, [orgUser, organization, setPageLoading]);

  if (!organization) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: '50vh' }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        ...(isDesktopJoinRequestsView && {
          height: (theme) =>
            `calc(100dvh - ${theme.spacing(defaultPageBoxPadding)})`,
          minHeight: 0,
          overflow: 'hidden',
        }),
      }}
    >
      {/* Sidebar - Desktop only */}
      {!isMobile && <OrganizationManagementSidebar />}

      {/* Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 1, sm: 2, md: 3 },
          ...(isDesktopJoinRequestsView && {
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
          }),
        }}
      >
        {/* Mobile Navigation Selector */}
        {isMobile && (
          <Select
            fullWidth
            value={mobileView}
            onChange={(e) => {
              const nextView = e.target.value as
                | 'details'
                | 'join-requests'
                | 'subscriptions';
              const nextPath =
                nextView === 'join-requests'
                  ? '/OrganizationManagement/OrganizationJoinRequests'
                  : nextView === 'subscriptions'
                    ? '/OrganizationManagement/OrganizationSubscriptions'
                    : '/OrganizationManagement/OrganizationManagementDetails';
              history.push(nextPath);
            }}
            sx={{
              mb: 2,
              backgroundColor: COLORS.WHITE,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.SAFEGUARD_BLUE,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.BLUE_CRAYOLA,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.BLUE_CRAYOLA,
              },
            }}
          >
            <MenuItem value="details">{t(StringKeys.DETAILS)}</MenuItem>
            <MenuItem value="join-requests">
              {t(StringKeys.JOIN_REQUESTS)}
            </MenuItem>
            <MenuItem value="subscriptions">
              {t(StringKeys.SUBSCRIPTIONS)}
            </MenuItem>
          </Select>
        )}

        {/* Routed Content (Desktop) or Conditional Content (Mobile) */}
        {!isMobile ? (
          <Switch>
            <Route
              exact
              path="/OrganizationManagement/OrganizationManagementDetails"
            >
              <OrganizationManagementDetails />
            </Route>

            <Route
              exact
              path="/OrganizationManagement/OrganizationJoinRequests"
            >
              <OrganizationJoinRequests />
            </Route>

            <Route
              exact
              path="/OrganizationManagement/OrganizationSubscriptions"
            >
              <OrganizationSubscriptions />
            </Route>

            {/* Default redirect */}
            <Redirect
              from="/OrganizationManagement"
              to="/OrganizationManagement/OrganizationManagementDetails"
            />
          </Switch>
        ) : (
          <>
            {mobileView === 'details' && <OrganizationManagementDetails />}
            {mobileView === 'join-requests' && <OrganizationJoinRequests />}
            {mobileView === 'subscriptions' && <OrganizationSubscriptions />}
          </>
        )}
      </Box>
    </Box>
  );
};

const RedirectingLoading = () => {
  const { t } = useTranslation();

  return <Loading loadingText={t(StringKeys.LOADING)} />;
};

const ProtectedOrganizationManagement = withAuthenticationRequired(
  OrganizationManagement,
  {
    onRedirecting: RedirectingLoading,
  },
);

export default ProtectedOrganizationManagement;
